import { InputSelect } from '@vrplatform/components';
import { useApiQuery } from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import { day, ensure, hasValue } from '@vrplatform/utils';
import { Box, Group, InputWrapper, rem } from '@mantine/core';
import { Text } from '@mantine/core';
import { useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { type ExpenseFormInputs, useExpenseForm } from '../useExpenseForm';

type Frequency =
  | NonNullable<ExpenseFormInputs['pattern']>['frequency']
  | 'no-repeat'
  | 'bi-weekly'
  | 'quarterly'
  | 'every-6-months';

type Props = {
  initialValues: ExpenseFormInputs | undefined;
};

const NO_REPEAT: SelectItem<Frequency> = {
  label: (
    <Group gap={rem(5)} justify="space-between" wrap="nowrap">
      <Text span>No repeat</Text>
      <Text span c="gray" size="xs">
        (One-time)
      </Text>
    </Group>
  ) as any,
  value: 'no-repeat' as const,
};

function useExpenseRepeatOptionsQuery({ initialValues }: Props) {
  const methods = useExpenseForm();

  const date = useWatch({
    control: methods.control,
    name: 'date',
  });

  const pattern = useWatch({
    control: methods.control,
    name: 'pattern',
  });

  return useApiQuery(
    async (api, signal, args) => {
      const base: SelectItem<Frequency>[] = (
        [
          initialValues?.pattern && initialValues.id ? undefined : NO_REPEAT,
          {
            label: 'Daily',
            value: 'daily' as const,
          },
          {
            label: 'Weekly',
            value: 'weekly' as const,
          },
          {
            label: 'Bi-weekly',
            value: 'bi-weekly' as const,
          },
          {
            label: 'Monthly',
            value: 'monthly' as const,
          },
          {
            label: 'Quarterly',
            value: 'quarterly' as const,
          },
          {
            label: 'Every 6 months',
            value: 'every-6-months' as const,
          },
          {
            label: 'Yearly',
            value: 'yearly' as const,
          },
        ] satisfies (SelectItem<Frequency> | undefined)[]
      ).filter(hasValue);

      if (!args.date) return base;

      const dates = await Promise.all([
        api.POST('/transactions/recurring-templates/preview', {
          body: {
            date: args.date,
            pattern: {
              frequency: 'daily',
            },
          },
          signal,
        }),
        api.POST('/transactions/recurring-templates/preview', {
          body: {
            date: args.date,
            pattern: {
              frequency: 'weekly',
            },
          },
          signal,
        }),
        api.POST('/transactions/recurring-templates/preview', {
          body: {
            date: args.date,
            pattern: {
              frequency: 'weekly',
              interval: 2,
            },
          },
          signal,
        }),
        api.POST('/transactions/recurring-templates/preview', {
          body: {
            date: args.date,
            pattern: {
              frequency: 'monthly',
            },
          },
          signal,
        }),
        api.POST('/transactions/recurring-templates/preview', {
          body: {
            date: args.date,
            pattern: {
              frequency: 'monthly',
              interval: 3,
            },
          },
          signal,
        }),
        api.POST('/transactions/recurring-templates/preview', {
          body: {
            date: args.date,
            pattern: {
              frequency: 'monthly',
              interval: 6,
            },
          },
          signal,
        }),
        api.POST('/transactions/recurring-templates/preview', {
          body: {
            date: args.date,
            pattern: {
              frequency: 'yearly',
            },
          },
          signal,
        }),
      ]).then((res) =>
        res.map((r) => {
          if (r.error) {
            throw r.error;
          }

          return r.data.nextTransactionInstanceAt;
        })
      );

      const hasNoRepeat = base[0].value === 'no-repeat';

      return base.map((b, idx) => {
        const nextInstanceAt = dates[hasNoRepeat ? idx - 1 : idx];

        return {
          ...b,
          label:
            b.value === 'no-repeat'
              ? b.label
              : ((
                  <Group gap={rem(5)} justify="space-between" wrap="nowrap">
                    <Text
                      sx={{
                        flexShrink: 0,
                      }}
                      span
                    >
                      {b.label}
                    </Text>

                    <Text span c="gray" size="xs" ta="right">
                      (Next on{' '}
                      {nextInstanceAt
                        ? day(nextInstanceAt).format('ddd, LL')
                        : '...'}
                      )
                    </Text>
                  </Group>
                ) as any),
        };
      });
    },
    {
      queryGroup: 'expenses',
      uniqueQueryKey: 'expense-repeat',
      variables: {
        date,
        frequency: pattern?.frequency,
        interval: pattern?.interval,
      },
      errorTitle: 'Error fetching recurring templates',
    }
  );
}

export const ExpenseRepeatInput = (props: Props) => {
  const methods = useExpenseForm();

  const queryData = useExpenseRepeatOptionsQuery(props);

  return (
    <Box>
      <Controller
        control={methods.control}
        name="pattern"
        render={({ field, fieldState }) => {
          // biome-ignore lint/correctness/useHookAtTopLevel: inside controller is fine
          const frequency = useMemo(() => {
            if (!field.value) return NO_REPEAT;

            return (
              field.value.frequency === 'monthly' && field.value.interval === 3
                ? 'quarterly'
                : field.value.frequency === 'monthly' &&
                    field.value.interval === 6
                  ? 'every-6-months'
                  : field.value.frequency === 'weekly' &&
                      field.value.interval === 2
                    ? 'bi-weekly'
                    : field.value.frequency
            ) satisfies Frequency;
          }, [field.value]);

          const value = !field.value
            ? NO_REPEAT
            : (queryData.data?.find((o) => o.value === frequency) ?? null);

          return (
            <InputWrapper required error={fieldState.error?.message}>
              <Box>
                <InputSelect
                  type="single"
                  data={{
                    options: queryData.data ?? [],
                    error: queryData.error,
                    loading: queryData.isInitialLoading,
                    sort: null,
                  }}
                  value={value}
                  inputProps={{
                    loadingQuery: queryData.isInitialLoading,
                  }}
                  dropdownProps={{
                    width: 'target',
                    hideSearch: true,
                  }}
                  setValue={(v) => {
                    if (!v?.value || v.value === NO_REPEAT.value) {
                      if (
                        props.initialValues?.pattern &&
                        props.initialValues.id
                      )
                        return; // do not allow reset when pattern is already set
                      field.onChange(null);
                    } else {
                      const lines = methods.getValues('lines');
                      for (const [idx, line] of Object.entries(lines)) {
                        if (line.reservationId) {
                          methods.setValue(
                            `lines.${Number(idx)}.reservationId`,
                            null
                          );
                        }
                      }
                      methods.setValue('paidStatus', 'unpaid');
                      methods.setValue('paidAt', null);

                      if (v.value === 'no-repeat') return field.onChange(null);

                      field.onChange(
                        ensure<(typeof field)['value']>({
                          frequency:
                            v.value === 'bi-weekly'
                              ? 'weekly'
                              : v.value === 'every-6-months'
                                ? 'monthly'
                                : v.value === 'quarterly'
                                  ? 'monthly'
                                  : v.value,
                          interval:
                            v.value === 'bi-weekly'
                              ? 2
                              : v.value === 'every-6-months'
                                ? 6
                                : v.value === 'quarterly'
                                  ? 3
                                  : 1,
                        })
                      );
                    }
                  }}
                />
              </Box>
            </InputWrapper>
          );
        }}
      />
    </Box>
  );
};
